import { cn } from "@/lib/utils";

/**
 * Los esqueletos de carga.
 *
 * ## Por qué aparecen recién ahora
 *
 * Con SSR no hacían falta: el HTML llegaba con los datos adentro, así que no
 * existía el momento «la pantalla está montada pero vacía». En una SPA ese
 * momento es inevitable, y sin esqueleto se ve como si la aplicación se
 * hubiera colgado.
 *
 * ## La regla al usarlos
 *
 * El esqueleto tiene que tener **la forma de lo que viene**. Un rectángulo
 * genérico donde después hay una tabla de ocho columnas hace que la pantalla
 * salte cuando llegan los datos, y ese salto se lee peor que la espera. Por eso
 * hay piezas por forma —lista, tabla, tarjetas— en vez de un solo bloque.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn("shimmer rounded-md", className)} />;
}

/**
 * El envoltorio accesible. El lector de pantalla anuncia que está cargando en
 * vez de leer una docena de cajas vacías, que es lo que pasa si se marcan los
 * bloques uno por uno.
 */
export function Loading({
  label = "Cargando…",
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    // El nombre va en `aria-label` y no solo en el texto oculto: el rol
    // `status` no toma su nombre del contenido, así que sin esto el lector
    // anuncia una región sin nombre.
    <div role="status" aria-label={label} aria-live="polite" aria-busy>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Cabecera de pantalla: título y bajada. */
export function HeaderSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-7 w-56" />
      <Skeleton className="h-4 w-80" />
    </div>
  );
}

/** Fila de indicadores, como la del inicio o la de analytics. */
export function TilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-card border-border bg-surface border p-4">
          <div className="flex items-start justify-between gap-2">
            <Skeleton className="size-9 rounded-lg" />
            <Skeleton className="h-8 w-16" />
          </div>
          <Skeleton className="mt-3 h-4 w-24" />
          <Skeleton className="mt-1.5 h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

/** Lista de filas con avatar y dos líneas: bandeja, contactos, agentes. */
export function ListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="rounded-card border-border bg-surface divide-border divide-y border">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 p-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-6 w-20 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Tabla con cabecera. `columns` marca el ancho real para que no salte. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="rounded-card border-border bg-surface overflow-hidden border">
      <div className="border-border bg-surface-muted flex gap-4 border-b px-4 py-2.5">
        {Array.from({ length: columns }, (_, index) => (
          <Skeleton key={index} className="h-3.5 flex-1" />
        ))}
      </div>
      <div className="divide-border divide-y">
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className="flex gap-4 px-4 py-3">
            {Array.from({ length: columns }, (_, column) => (
              <Skeleton key={column} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Rejilla de tarjetas: propiedades, segmentos, campañas. */
export function CardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="rounded-card border-border bg-surface space-y-3 border p-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Pantalla entera: cabecera + contenido. Lo que usa la mayoría de las vistas. */
export function PageSkeleton({ children }: { children?: React.ReactNode }) {
  return (
    <Loading>
      <div className="space-y-6">
        <HeaderSkeleton />
        {children ?? <ListSkeleton />}
      </div>
    </Loading>
  );
}
